import { _getInstance } from '@firebase/auth/internal'
import { FirebaseError } from '@firebase/util'
import {
  Auth,
  AuthProvider,
  EmailAuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  PhoneAuthProvider,
  User,
  UserCredential,
  browserPopupRedirectResolver,
  signOut as authSignOut,
  linkWithCredential,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth'
import { auth } from '~/src/lib/firebase/firebaseAuthInstance'
import {
  linkPendingCredential,
  setPendingLink,
} from '~/src/lib/firebase/pendingLink'
import logger from '~/src/lib/logger'

export {
  GoogleAuthProvider,
  FacebookAuthProvider,
  EmailAuthProvider,
  sendPasswordResetEmail,
}

export const isValidEmail = (email?: string): boolean => {
  if (!email) return false
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return pattern.test(email)
}

/*
AnonymousProvider used to match the AuthProvider interface
from Firebase for the anonymous login option
*/
export class AnonymousProvider implements AuthProvider {
  static readonly PROVIDER_ID = 'anonymous'
  providerId = AnonymousProvider.PROVIDER_ID
}

// Google Provider Setup
const providerGoogle = new GoogleAuthProvider()
providerGoogle.setCustomParameters({
  prompt: 'select_account',
})
// https://developers.google.com/identity/protocols/oauth2/scopes
providerGoogle.addScope('https://www.googleapis.com/auth/userinfo.email')
providerGoogle.addScope('https://www.googleapis.com/auth/userinfo.profile')

// Apple Provider Setup
const providerApple = new OAuthProvider('apple.com')
providerApple.addScope('email')
providerApple.addScope('name')

// Facebook Provider Setup. `display=popup` asks Meta for its compact popup
// layout; `email` can still come back empty for phone-registered FB accounts.
const providerFacebook = new FacebookAuthProvider()
providerFacebook.addScope('email')
providerFacebook.addScope('public_profile')
providerFacebook.setCustomParameters({ display: 'popup' })

/* AppleProvider used currently to detect when the user
has an existing Apple login linked to their Firebase account */
export class AppleProvider implements AuthProvider {
  static readonly PROVIDER_ID = 'apple.com'
  providerId = AppleProvider.PROVIDER_ID
}

const phoneProvider =
  typeof window !== 'undefined'
    ? new PhoneAuthProvider(auth)
    : ({} as AuthProvider)

export type SupportedProviderId =
  | typeof GoogleAuthProvider.PROVIDER_ID
  | typeof PhoneAuthProvider.PROVIDER_ID
  | typeof AppleProvider.PROVIDER_ID
  | typeof FacebookAuthProvider.PROVIDER_ID
  | typeof EmailAuthProvider.PROVIDER_ID

export const SUPPORTED_PROVIDER_MAPPING: {
  [key in SupportedProviderId]: AuthProvider
} = {
  [GoogleAuthProvider.PROVIDER_ID]: providerGoogle,
  [PhoneAuthProvider.PROVIDER_ID]: phoneProvider,
  [AppleProvider.PROVIDER_ID]: providerApple,
  [FacebookAuthProvider.PROVIDER_ID]: providerFacebook,
  [EmailAuthProvider.PROVIDER_ID]: new EmailAuthProvider(),
}

export const ACCOUNT_EXISTS_ERROR_CODE =
  'auth/account-exists-with-different-credential'

export interface SignInReturnValue {
  credential: UserCredential | undefined
}

export const signinWithEmail = async ({
  email,
  password,
}: {
  email: string
  password: string
}): Promise<UserCredential | undefined> => {
  if (!email || !password) {
    throw new FirebaseError(
      'auth/invalid-email',
      'Email and password are required'
    )
  }

  try {
    return await signInWithEmailAndPassword(auth, email, password)
  } catch (error: any) {
    logger.debug('[signinWithEmail] error', { error })
    throw error
  }
}

// The internal surface of the resolver instance that signInWithPopup uses.
interface WarmablePopupRedirectResolver {
  _shouldInitProactively: boolean
  _initialize: (auth: Auth) => Promise<unknown>
}

/**
 * Load Firebase's auth iframe before the user taps Google/Apple (DUP-2646).
 *
 * `signInWithPopup` must reach `window.open` without a network wait, or iOS
 * Safari and in-app browsers no longer count it as a tap and block the popup.
 * Firebase normally loads the iframe at startup on those browsers, but only
 * when `initializeAuth` gets a `popupRedirectResolver` — and
 * `firebaseAuthInstance` deliberately omits it to keep the iframe off page
 * load. So we warm the same resolver instance once sign-in UI is on screen,
 * gated on Firebase's own browser check.
 */
export const warmPopupSignIn = (): void => {
  if (typeof window === 'undefined') return
  try {
    const resolver = _getInstance<WarmablePopupRedirectResolver>(
      browserPopupRedirectResolver
    )
    if (!resolver._shouldInitProactively) return
    resolver._initialize(auth).catch((error: unknown) => {
      logger.debug('[warmPopupSignIn] iframe init failed', { error })
    })
  } catch (error) {
    logger.debug('[warmPopupSignIn] error', { error })
  }
}

export const signInWithProvider = async ({
  providerId,
}: {
  providerId: SupportedProviderId
}): Promise<UserCredential | undefined> => {
  try {
    const credential = await signInWithPopup(
      auth,
      SUPPORTED_PROVIDER_MAPPING[providerId],
      browserPopupRedirectResolver
    )
    await linkPendingCredential(credential.user)
    return credential
  } catch (error: any) {
    switch (error?.code) {
      case 'auth/cancelled-popup-request':
      case 'auth/popup-closed-by-user':
        return undefined
      case 'auth/credential-already-in-use': {
        const pendingCred = OAuthProvider.credentialFromError(error)
        const currentUser = auth.currentUser

        if (pendingCred && currentUser) {
          // sign in to existing account
          return await signInWithCredential(auth, pendingCred)
        }

        throw error
      }
      case 'auth/email-already-in-use':
      case 'auth/account-exists-with-different-credential': {
        const pendingCred = OAuthProvider.credentialFromError(error)
        const email = error.customData?.email as string | undefined
        const currentUser = auth.currentUser

        // try to link the social account to the account already signed in
        if (pendingCred && currentUser && !currentUser.isAnonymous) {
          await linkWithCredential(currentUser, pendingCred)
          return undefined
        }

        // Hold the credential and ask the person to sign in the way they did
        // before; `linkPendingCredential` attaches it once they do. Email
        // enumeration protection hides which provider owns the email, so the
        // message cannot name it.
        setPendingLink({
          providerId,
          credential: pendingCred ?? undefined,
          email,
        })

        logger.warn('Social sign-in collided with an existing account', {
          code: error.code,
          providerId,
          canLink: !!(pendingCred && email),
        })

        throw new FirebaseError(
          ACCOUNT_EXISTS_ERROR_CODE,
          `Account exists for ${providerId}`,
          { ...error.customData, email, pendingCred }
        )
      }
      default:
        throw error
    }
  }
}

export const signInAnonymous = async (): Promise<User | undefined> => {
  try {
    const currentUser = auth.currentUser

    if (currentUser) {
      return currentUser
    }

    const userCredential = await signInAnonymously(auth)
    return userCredential.user
  } catch (error: any) {
    logger.debug('[signInAnonymous] error', { error })
    throw error
  }
}

export const signOut = async () => {
  await authSignOut(auth)
}

export default auth
